import React, { memo, useState } from "react";
import {
    faMagnifyingGlass,
    faRotateLeft,
    faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface SearchFiltersProps {
    query: string;
    email: string;
    phone: string;
    clinic: string;
    firstName: string;
    lastName: string;
    currentWeek: string;
    accountStatus: string;
    registerState: string;
    onboardingStatus: string;
    showRegistrationPath: boolean;
    setQuery: (value: string) => void;
    setEmail: (value: string) => void;
    setPhone: (value: string) => void;
    setClinic: (value: string) => void;
    setFirstName: (value: string) => void;
    setLastName: (value: string) => void;
    setCurrentWeek: (value: string) => void;
    setAccountStatus: (value: string) => void;
    setRegisterState: (value: string) => void;
    setOnboardingStatus: (value: string) => void;
    setShowRegistrationPath: (value: boolean) => void;
    onSearch: () => void;
    onReset: () => void;
}

const SearchFilters: React.FC<SearchFiltersProps> = (props) => {
    const [advanced, setAdvanced] = useState(false);
    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        props.onSearch();
    };

    return (
        <Card className="mb-3 user-filter-card">
            <Card.Body>
                <div className="user-filter-heading">
                    <div>
                        <span>User directory</span>
                        <h2>Search users</h2>
                        <p>
                            Search visible table fields or narrow results with
                            program and clinic filters.
                        </p>
                    </div>
                    <div className="user-filter-heading-actions">
                        <Form.Check
                            type="checkbox"
                            id="show-registration-path"
                            label="Show registration path"
                            checked={props.showRegistrationPath}
                            onChange={(event) =>
                                props.setShowRegistrationPath(
                                    event.target.checked,
                                )
                            }
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-filter-advanced"
                            onClick={() => setAdvanced((value) => !value)}
                        >
                            <FontAwesomeIcon icon={faSliders} />
                            {advanced ? "Hide advanced" : "Advanced filters"}
                        </Button>
                    </div>
                </div>
                <Form onSubmit={submit}>
                    <div className="user-filter-primary">
                        <Form.Group>
                            <Form.Label>Search table</Form.Label>
                            <Form.Control
                                value={props.query}
                                onChange={(event) =>
                                    props.setQuery(event.target.value)
                                }
                                placeholder="ID, name, username, insurance ID…"
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={props.email}
                                onChange={(event) =>
                                    props.setEmail(event.target.value)
                                }
                                placeholder="name@example.com"
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Mobile</Form.Label>
                            <Form.Control
                                value={props.phone}
                                onChange={(event) =>
                                    props.setPhone(event.target.value)
                                }
                                placeholder="Phone number"
                            />
                        </Form.Group>
                        <div className="user-filter-actions">
                            <Button
                                type="submit"
                                className="user-filter-search"
                            >
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                                Search
                            </Button>
                            <Button
                                type="button"
                                variant="light"
                                className="user-filter-reset"
                                onClick={props.onReset}
                            >
                                <FontAwesomeIcon icon={faRotateLeft} />
                                Reset
                            </Button>
                        </div>
                    </div>
                    {advanced && (
                        <div className="user-filter-advanced-grid">
                            <Form.Group>
                                <Form.Label>First name</Form.Label>
                                <Form.Control
                                    value={props.firstName}
                                    onChange={(event) =>
                                        props.setFirstName(event.target.value)
                                    }
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Last name</Form.Label>
                                <Form.Control
                                    value={props.lastName}
                                    onChange={(event) =>
                                        props.setLastName(event.target.value)
                                    }
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Current week</Form.Label>
                                <Form.Select
                                    value={props.currentWeek}
                                    onChange={(event) =>
                                        props.setCurrentWeek(event.target.value)
                                    }
                                >
                                    <option value="all">All weeks</option>
                                    {Array.from({ length: 22 }, (_, index) => (
                                        <option
                                            key={index + 1}
                                            value={index + 1}
                                        >
                                            Week {index + 1}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Account status</Form.Label>
                                <Form.Select
                                    value={props.accountStatus}
                                    onChange={(event) =>
                                        props.setAccountStatus(
                                            event.target.value,
                                        )
                                    }
                                >
                                    <option value="all">All statuses</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Onboarding status</Form.Label>
                                <Form.Select
                                    value={props.onboardingStatus}
                                    onChange={(event) =>
                                        props.setOnboardingStatus(
                                            event.target.value,
                                        )
                                    }
                                >
                                    <option value="all">All stages</option>
                                    <option value="referral">Referral</option>
                                    <option value="invitation">
                                        Invitation
                                    </option>
                                    <option value="app_install">
                                        App Install
                                    </option>
                                    <option value="account_setup">
                                        Account Setup
                                    </option>
                                    <option value="eligibility">
                                        Eligibility
                                    </option>
                                    <option value="questionnaires">
                                        Questionnaires
                                    </option>
                                    <option value="baseline_week">
                                        Baseline Week
                                    </option>
                                    <option value="program_started">
                                        Program Started
                                    </option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Registration state</Form.Label>
                                <Form.Select
                                    value={props.registerState}
                                    onChange={(event) =>
                                        props.setRegisterState(
                                            event.target.value,
                                        )
                                    }
                                >
                                    <option value="all">All states</option>
                                    <option value="preparing">Preparing</option>
                                    <option value="pending">Pending</option>
                                    <option value="accepted">Accepted</option>
                                    <option value="finished">Finished</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="personal_rejected">
                                        Personal rejected
                                    </option>
                                    <option value="questionnaires">
                                        Questionnaires
                                    </option>
                                    <option value="plan_purchased">
                                        Plan purchased
                                    </option>
                                    <option value="set_password">
                                        Set password
                                    </option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="user-filter-clinic">
                                <Form.Label>Clinic</Form.Label>
                                <Form.Control
                                    value={props.clinic}
                                    onChange={(event) =>
                                        props.setClinic(event.target.value)
                                    }
                                    placeholder="Clinic name, doctor, number or ID"
                                />
                            </Form.Group>
                        </div>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );
};

export default memo(SearchFilters);
